<template>
  <component :is="el" :label="label" :required="required" :error="error">

    <!-- XOL-67 (B3): only with `error-id`, the error message carries that id (Element's own error slot, same markup as its
         default), so the inner input can point `aria-describedby` at it. Without it Element's default message is unchanged. -->
    <template v-if="errorId" #error="slotProps">
      <div class="el-form-item__error" :id="errorId">{{ slotProps.error }}</div>
    </template>

    <span v-if="noForm">{{label}}&nbsp;</span>

    <el-select
      v-model="formData"
      :disabled="disabled"
      :placeholder="placeholder"
      value-key="value"
      :size="size"
      :filterable="filterable"
      @change="updated">

      <el-option-group
        v-for="group in shownGroups"
        :key="group.key || group.label"
        :label="group.label">
<!--
        <span :style="'float: left; width: 100%; background-color: ' + getCatColour(group.label) + ';'">{{ group.label }}
          <span style="float: right; color: #8492a6; margin-right: 5px; font-size: 12px" :class="getCatIcon(group.label)" />
        </span>
-->
        <el-option
          v-for="item in group.options"
          :key="item.value"
          :label="item.label"
          :value="item.value">
          <span :style="'float: left; width: 100%; background-color: ' + getCatColour(item.category) + ';'">{{ item.label }}
            <small v-if="item.own">(your organisation)</small>
            <span style="float: right; color: #8492a6; margin-right: 5px; font-size: 12px" :class="getCatIcon(item.category)" />
          </span>
        </el-option>

      </el-option-group>

    </el-select>
  </component>
</template>

<script>

import ArrayMixins from '@/mixins/ArrayMixins'
import { normaliseOrgCategory } from '@/lib/contactRoles'

export default {
  name: 'select-organisation',

  mixins: [ArrayMixins],

  props: {

    label: {
      type: String,
      default: 'Organisation'
    },

    value: {
//      type: String,
      required: true
    },

    type: {
      type: [Array, String],
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },

    size: {
      type: String,
      default: 'default'
    },

    required: {
      type: Boolean,
      default: false
    },

    placeholder: {
      type: String,
      default: 'Select Organisation'
    },

    helpText: {
      type: String,
      default: null
    },

    sharerLimits: {
      type: Array,
      default: null
    },

    fullObject: {
      type: Boolean,
      default: false
    },

    noForm: {
      type: Boolean,
      default: false
    },

    // XOL-67 (B2): all additive; each default keeps every existing call site as it was.
    //
    // A group with the user's own organisation, "{org} (your organisation)", ABOVE the groups of
    // `type`. The organisation is read from the store when the dropdown renders (a computed),
    // not captured at creation. It is listed once: it leaves the groups it would also be in.
    includeOwn: {
      type: Boolean,
      default: false
    },

    // The el-form-item's plain `error` String prop: a display watcher independent of :model /
    // :rules / validate() (the XOL-487 pattern, as SelectOrg). null, not '', so Vue leaves the
    // attribute off when `el` is the bare div of no-form mode.
    error: {
      type: String,
      default: null
    },

    // Type to narrow the list (the el-select's own filter on the option labels).
    filterable: {
      type: Boolean,
      default: false
    },

    // XOL-67 (B3), accessibility, both additive and inert when not given. Element's el-select
    // renders its own <input> and passes no attribute to it, so these are put on that input
    // (`applyInputAttrs`): `ariaLabel` is its accessible name, and `errorId` (the id of the error
    // message) makes a non-empty `error` read as `aria-invalid` + `aria-describedby`.
    ariaLabel: {
      type: String,
      default: null
    },

    errorId: {
      type: String,
      default: null
    }

  },


  computed: {

    // The user's own organisation, or null when `include-own` is off or the store has not got
    // one yet (a computed: the store hydrates after the first paint).
    ownOrg () {
      if (!this.includeOwn || typeof this.org !== 'function') {
        return null
      }

      let org = this.org()

      return org && org.id !== undefined && org.id !== null && org.id !== '' ? org : null
    },

    // What the dropdown lists: the own-organisation group, then `groups`. Without `include-own`
    // (or without an own organisation) this IS `groups`, the same array.
    shownGroups () {
      let own = this.ownOrg

      if (!own || !Array.isArray(this.groups)) {
        return this.groups
      }

      let category = normaliseOrgCategory(own.category)
      let rest = []

      this.groups.forEach(function (group) {
        if (!group || !Array.isArray(group.options)) {
          rest.push(group)
          return
        }

        let options = group.options.filter(function (item) { return item.value !== own.id })

        // A group that held nothing but the own organisation has nothing left to list.
        if (options.length > 0 || group.options.length === 0) {
          rest.push(Object.assign({}, group, { options: options }))
        }
      })

      return [{
        key: 'own-organisation',
        label: category || 'Your organisation',
        options: [{ value: own.id, label: own.name, category: category, data: own, own: true }]
      }].concat(rest)
    }
  },


  mounted () {
    this.applyInputAttrs()
  },


  // The `updated` LIFECYCLE hook (not `methods.updated` below, which is the select's change handler).
  // `error` is a prop the template reads, so a change re-renders this component and lands here.
  updated () {
    this.applyInputAttrs()
  },


  beforeDestroy () {
    this.unwatchInput()
  },


  methods: {

    // The accessibility attributes of el-select's inner <input> (see `ariaLabel`, `errorId`). Set and
    // removed on the element itself, because el-select exposes no way to pass them through.
    //
    // Element's own el-input re-renders that <input> (its `:aria-label="label"` binding is undefined here),
    // and Vue's patch then removes an `aria-label` it did not put there, without this component re-rendering.
    // So the element is also watched, and an attribute that goes is put back (`watchInput`). (A `for` / `id` pair
    // would name the input without this, but `aria-invalid` and `aria-describedby` cannot be given to el-select's
    // input any way but this, so the observer stays.)
    applyInputAttrs () {
      // Inert for every caller that gives neither: nothing is set, removed or watched.
      if (!this.ariaLabel && !this.errorId) {
        return
      }

      let input = this.$el && typeof this.$el.querySelector === 'function' ? this.$el.querySelector('input') : null

      if (!input) {
        return
      }

      this.watchInput(input)

      // Setting an attribute to the value it has is still a mutation record: only touch what differs.
      let set = (name, value) => {
        if (value) {
          if (input.getAttribute(name) !== value) {
            input.setAttribute(name, value)
          }
        } else if (input.hasAttribute(name)) {
          input.removeAttribute(name)
        }
      }

      if (this.ariaLabel) {
        set('aria-label', this.ariaLabel)
      }

      if (this.errorId) {
        set('aria-invalid', this.error ? 'true' : null)
        set('aria-describedby', this.error ? this.errorId : null)
      }
    },

    // Watch the input for one of our attributes being changed behind our back, and put them right again.
    watchInput (input) {
      if (this.watchedInput === input || typeof MutationObserver === 'undefined') {
        return
      }

      this.unwatchInput()
      this.watchedInput = input
      this.attrObserver = new MutationObserver(() => { this.applyInputAttrs() })
      this.attrObserver.observe(input, { attributes: true, attributeFilter: ['aria-label', 'aria-invalid', 'aria-describedby'] })
    },

    unwatchInput () {
      if (this.attrObserver) {
        this.attrObserver.disconnect()
      }

      this.attrObserver = null
      this.watchedInput = null
    },

    getCatMetaData (cat, type) {

      switch (cat) {

        case 'Backhaul':
          return (type === 'icon' ? 'fa fa-wifi' : 'ghostwhite')

        case 'Contractor':
          return (type === 'icon' ? 'fa fa-tools' : 'ivory')

        case 'Telco':
          return (type === 'icon' ? 'fa fa-users' : 'azure')

        case 'Tower Supplier':
          return (type === 'icon' ? 'fa fa-broadcast-tower' : 'LavenderBlush')

        case 'Tower Company':
          return (type === 'icon' ? 'fa fa-address-card' : 'aliceblue')

        default:
          return ''
      }

    },

    getCatColour (cat) {
      return this.getCatMetaData(cat, 'color')
    },


    getCatIcon (cat) {
      return this.getCatMetaData(cat, 'icon')
    },

    // `cat` is null (every group), a category name (that group's options, flat), or an Array
    // of names (XOL-67): the groups of those categories, in the order the Array gives them. A
    // name that has no group is skipped, so an Array that matches nothing is an empty list
    // (never every organisation); an empty Array is "no restriction", as null.
    selectCategory (list, cat) {
      let org = list

      if (Array.isArray(cat)) {
        if (cat.length === 0 || !Array.isArray(list)) {
          return org
        }

        org = []

        cat.forEach(function (name) {
          for (let i = 0; i < list.length; i++) {
            if (name && list[i].label.toLowerCase() === String(name).toLowerCase() && org.indexOf(list[i]) === -1) {
              org.push(list[i])
            }
          }
        })

        return org
      }

      for (let i = 0; i < list.length; i++) {

        if (cat && list[i].label.toLowerCase() === cat.toLowerCase()) {
          org = list[i].options
        }
      }

      return org
    },



    findObj (value) {
      let list = this.shownGroups
      let obj = null

      for (let i = 0; i < list.length; i++) {

        if (list[i].options) {

          for (let i2 = 0; i2 < list[i].options.length; i2++) {
            if (list[i].options[i2].value === value) {
              obj = list[i].options[i2].data
            }
          }

        } else {

          if (list[i].value === value) {
            obj = list[i].data
          }
        }
      }

      return obj
    },


    updated (value) {
      let obj = this.findObj(value)

      this.$emit('input', this.fullObject ? obj : value)
      this.$emit('change', this.fullObject ? obj : value)
    }
  },

  data () {
    return {
      formData: this.value,
      groups: this.selectCategory(this.getConfig().orgGroups, this.type),
      el: this.noForm ? 'div' : 'el-form-item'
    }  // data
  }
}
</script>
